import {
  deleteSearch,
  getSearchList,
  updateSearchSortingPosition,
} from "@/api/endpoints/searches";
import {
  SidebarGroup,
  SidebarGroupLabel,
  SidebarGroupContent,
  SidebarMenu,
} from "@components/ui/sidebar";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ChevronRightIcon } from "lucide-react";
import { Trans, useLingui } from "@lingui/react/macro";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@components/ui/collapsible";
import { useLocalStorage } from "@/hooks/useLocalStorage";
import { useState } from "react";
import { Spinner } from "@components/ui/spinner";
import ErrorComponent from "@components/ui/error";
import { ActiveSearches } from "./ActiveSearches";
import { SavedSearches } from "./SavedSearches";
import { InactiveSearches } from "./InactiveSearches";
import { EmptySearches } from "./EmptySearches";
import AccessControl from "@components/AccessControl";
import { Feature } from "@components/Feature";
import { FEATURE } from "@/features";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@components/ui/alert-dialog";
import { postTrackingData } from "@/api/endpoints/track";
import { toast } from "sonner";
import { EVENT } from "@/tracking_events";
import { generatePath, useHistory } from "react-router-dom";
import { ROUTES } from "@/routes_new";
import { useGroupedSearches } from "@/hooks/useGroupedSearches";
import { SearchLight } from "@/types/Search";
import { SuccessfulResponse } from "@/api/fetcher_v2";

export function SearchesNav({
  orgUuid,
  pathname,
}: {
  orgUuid: string;
  pathname: string;
}) {
  const { t } = useLingui();

  // URL state
  const history = useHistory();

  // Local storage state
  const [isSearchesOpen, setIsSearchesOpen] = useLocalStorage(
    "sidebar-searches-open",
    true
  );

  // Local state
  const [searchToDelete, setSearchToDelete] = useState<string | undefined>();

  // Server state
  const queryClient = useQueryClient();

  const {
    data: searches,
    isLoading: isSearchesLoading,
    error: searchesError,
  } = useQuery({
    queryKey: [orgUuid, "searches"],
    queryFn: () => getSearchList({ light: true }),
    refetchOnWindowFocus: false,
  });

  const { activeSearches, savedSearches, inactiveSearches, hasSearches } =
    useGroupedSearches(searches);

  const { mutate: mutateDeleteSearch } = useMutation({
    mutationFn: deleteSearch,
    onSuccess: (response, deletedSearchUuid) => {
      if (response.ok) {
        history.replace(generatePath(ROUTES.MENTION_LIST_ORGID, { orgUuid }));
        postTrackingData(EVENT.DELETE_ALERT, { uuid: deletedSearchUuid });
        queryClient.invalidateQueries({ queryKey: [orgUuid, "searches"] });
        toast.success(t`Bevakningen har raderats`);
        window.location.reload();
      } else {
        toast.error(t`Lyckades inte radera bevakningen`);
      }
    },
    onError: () => {
      toast.error(t`Lyckades inte radera bevakningen`);
    },
  });

  const { mutate: mutateReorderSearches } = useMutation({
    mutationFn: (reorderedSearches: SearchLight[]) =>
      Promise.all(
        reorderedSearches.map((search, index) =>
          updateSearchSortingPosition(search.uuid, index)
        )
      ),
    onMutate: async (reorderedSearches) => {
      // Cancel outgoing refetches so they don't overwrite our optimistic update
      await queryClient.cancelQueries({ queryKey: [orgUuid, "searches"] });

      // Snapshot previous value for rollback
      const previousData = queryClient.getQueryData<
        SuccessfulResponse<SearchLight[]>
      >([orgUuid, "searches"]);

      // Optimistically update the cache
      if (previousData?.ok) {
        const updatedData = previousData.data.map((search) => {
          const newIndex = reorderedSearches.findIndex(
            (s) => s.id === search.id
          );
          if (newIndex !== -1) {
            return { ...search, sorting_position: newIndex };
          }
          return search;
        });

        queryClient.setQueryData<SuccessfulResponse<SearchLight[]>>(
          [orgUuid, "searches"],
          {
            ...previousData,
            ok: true,
            data: updatedData,
          }
        );
      }

      return { previousData };
    },
    onSuccess: (res) => {
      if (res.some((r) => !r.ok)) {
        throw new Error();
      }
    },
    onError: (_err, _variables, context) => {
      // Rollback to previous data on error
      if (context?.previousData) {
        queryClient.setQueryData([orgUuid, "searches"], context.previousData);
      }
      toast.error(t`Kunde inte spara ordningen`);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: [orgUuid, "searches"] });
    },
  });

  return (
    <>
      <Collapsible
        open={isSearchesOpen}
        onOpenChange={setIsSearchesOpen}
        className="group/searches"
        data-testid="mention-side-nav"
      >
        <SidebarGroup>
          <SidebarGroupLabel asChild>
            <CollapsibleTrigger>
              <Trans>Bevakningar</Trans>
              <ChevronRightIcon className="ml-auto transition-transform group-data-[state=open]/searches:rotate-90" />
            </CollapsibleTrigger>
          </SidebarGroupLabel>
          <CollapsibleContent>
            {searches?.ok ? (
              hasSearches ? (
                <SidebarGroupContent>
                  <SidebarMenu>
                    <ActiveSearches
                      activeSearches={activeSearches}
                      orgUuid={orgUuid}
                      pathname={pathname}
                      onDelete={setSearchToDelete}
                      onReorder={mutateReorderSearches}
                    />

                    <Feature required={FEATURE.HAS_SEARCH_PRODUCT} upsale>
                      <AccessControl>
                        {savedSearches.length > 0 ? (
                          <SavedSearches
                            savedSearches={savedSearches}
                            orgUuid={orgUuid}
                            pathname={pathname}
                            onDelete={setSearchToDelete}
                          />
                        ) : null}
                      </AccessControl>
                    </Feature>

                    {inactiveSearches.length > 0 ? (
                      <InactiveSearches
                        inactiveSearches={inactiveSearches}
                        orgUuid={orgUuid}
                        pathname={pathname}
                        onDelete={setSearchToDelete}
                      />
                    ) : null}
                  </SidebarMenu>
                </SidebarGroupContent>
              ) : (
                <EmptySearches orgUuid={orgUuid} />
              )
            ) : isSearchesLoading ? (
              <Spinner className="mx-auto" />
            ) : (
              <ErrorComponent
                className="py-4 md:py-6"
                error={searchesError}
                heading={t`Åh nej!`}
                description={t`Något gick snett med att ladda bevakningarna. Vänligen försök igen senare`}
              />
            )}
          </CollapsibleContent>
        </SidebarGroup>
      </Collapsible>

      <AlertDialog
        open={!!searchToDelete}
        onOpenChange={() => {
          setSearchToDelete(undefined);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              <Trans>Radera bevakning</Trans>
            </AlertDialogTitle>
            <AlertDialogDescription>
              <Trans>
                Detta kommer att radera hela bevakningen, inklusive omnämnanden.
                Är du säker?
              </Trans>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>
              <Trans>Avbryt</Trans>
            </AlertDialogCancel>
            <AlertDialogAction
              variant="destructive"
              onClick={() => {
                mutateDeleteSearch(searchToDelete!);
              }}
            >
              <Trans>Bekräfta</Trans>
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
